import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import {
	createFileRoute,
	redirect,
	useNavigate,
	useRouteContext,
} from "@tanstack/react-router";
import {
	ArrowDown,
	ArrowUp,
	CalendarClock,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	FileSpreadsheet,
	FileText,
	Loader2,
	MessageSquareText,
	Phone,
	Send,
	Trash2,
	X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { BulkActionsBar } from "#/components/admin/bulk-actions-bar";
import { ExportDialog } from "#/components/admin/export-dialog";
import {
	FilterFields,
	type Option,
	SearchInput,
	useDismiss,
} from "#/components/admin/filter-controls";
import { SendTemplateDialog } from "#/components/admin/send-template-dialog";
import { TemplateManagerDialog } from "#/components/admin/template-manager-dialog";
import { LeadStatusModal } from "#/components/lead-status-modal";
import { authClient } from "#/lib/auth-client";
import { editionLabel, isoDay } from "#/lib/editions";
import {
	ASSIGNEE_SOURCE_HINTS,
	type LeadAssigneeView,
} from "#/lib/lead-assignees";
import { exportColumnsFor } from "#/lib/lead-export";
import {
	algiersToday,
	countActiveFilters,
	LEAD_SORT_FIELDS,
	type LeadFilters,
	type LeadSortField,
	leadFiltersSchema,
	NO_EDITION,
	NO_EDITION_LABEL,
	SORT_LABELS,
} from "#/lib/lead-filters";
import {
	type LeadOrigin,
	ORIGIN_LABELS,
	ORIGIN_STYLES,
} from "#/lib/lead-origin";
import {
	DELIVERY_MODE_LABELS,
	DELIVERY_MODE_STYLES,
	type DeliveryMode,
	GROUP_TYPE_LABELS,
	GROUP_TYPE_STYLES,
	type GroupType,
} from "#/lib/lead-preferences";
import {
	type LeadStatus,
	STATUS_CATEGORIES,
	STATUS_CODES,
	STATUS_LABELS,
	STATUS_STYLES,
} from "#/lib/lead-status";
import { whatsAppLink } from "#/lib/message-templates";
import {
	canDeleteLeads,
	canOpenOrdersPage,
	homeRouteFor,
	isCommunityManager,
} from "#/lib/roles";
import { getSession } from "#/lib/session";

// Everything the table is showing lives in the URL, so a rep can bookmark
// "my new leads in Alger" or paste the link to a colleague. All keys are
// optional: a bare /admin/leads means "newest first, no filters".
const leadsSearchSchema = leadFiltersSchema.extend({
	sortBy: z.enum(LEAD_SORT_FIELDS).optional(),
	sortDir: z.enum(["asc", "desc"]).optional(),
	page: z.coerce.number().int().min(1).optional(),
	pageSize: z.coerce.number().int().min(10).max(200).optional(),
});

type LeadsSearch = z.infer<typeof leadsSearchSchema>;

export const Route = createFileRoute("/admin/leads")({
	beforeLoad: async () => {
		const session = await getSession();
		if (!session?.user) {
			throw redirect({ to: "/admin/login" });
		}
		// Every role but the designer may open this page — a community manager
		// gets a read-only view scoped to their own leads (see
		// `leadsReadProcedure` and the `readOnly` branch in LeadsPage below).
		if (!canOpenOrdersPage(session.user.role)) {
			throw redirect({ to: homeRouteFor(session.user.role) });
		}
	},
	// A malformed hand-edited URL should degrade to the default view rather than
	// throw the whole route.
	validateSearch: (search: Record<string, unknown>): LeadsSearch => {
		const parsed = leadsSearchSchema.safeParse(search);
		return parsed.success ? parsed.data : {};
	},
	component: LeadsPage,
});

type LeadRow = {
	id: string;
	fullName: string;
	phone: string;
	email: string | null;
	wilaya: string | null;
	jobStatus: string | null;
	status: LeadStatus;
	// Present only for admins — the server strips these for other roles.
	origin?: LeadOrigin;
	createdBy?: { id: string; name: string } | null;
	deliveryMode: DeliveryMode | null;
	groupType: GroupType | null;
	notes: string | null;
	source: string | null;
	createdAt: string | Date;
	nextFollowUpAt: string | Date | null;
	lastContactedAt: string | Date | null;
	trainingId: string;
	editionId: number | null;
	edition: { editionNumber: number } | null;
	assignees: LeadAssigneeView[];
};

const PAGE_SIZES = [25, 50, 100, 200];

/**
 * The actions column is pinned to the table's inline-end edge — the LEFT edge,
 * since the dashboard runs RTL. The table is `min-w-[1000px]` inside an
 * `overflow-x-auto` card, and RTL scroll starts pinned to the right, so a long
 * status label or a narrow laptop used to push this column off-screen with no
 * hint it was there: an admin reported the delete button "missing" when in fact
 * the whole column (call, WhatsApp, template, delete) had scrolled away.
 *
 * `end-0` is the logical edge, so the pin itself follows the writing direction
 * (`--shadow-pinned` does not: its offset assumes the RTL side). Each cell
 * supplies its own OPAQUE background — a translucent one would let the columns
 * it floats over show through while scrolling.
 */
const STICKY_ACTIONS =
	"sticky end-0 z-10 border-s border-line px-4 py-3 text-start shadow-pinned";

function formatDate(value: string | Date) {
	return new Intl.DateTimeFormat("ar-DZ", {
		dateStyle: "medium",
		timeStyle: "short",
		timeZone: "Africa/Algiers",
	}).format(new Date(value));
}

function formatDay(value: string | Date) {
	return new Intl.DateTimeFormat("ar-DZ", {
		dateStyle: "medium",
		timeZone: "Africa/Algiers",
	}).format(new Date(value));
}

/** "منذ 3 أيام" — cheaper to scan than an absolute date in a call list. */
function relativeDays(value: string | Date) {
	const then = new Date(value).getTime();
	const days = Math.round((Date.now() - then) / 86_400_000);
	const rtf = new Intl.RelativeTimeFormat("ar-DZ", { numeric: "auto" });
	if (Math.abs(days) < 1) return "اليوم";
	return rtf.format(-days, "day");
}

function LeadsPage() {
	const { trpc } = useRouteContext({ from: "__root__" });
	// Role decides which export columns are offered; the server independently
	// strips the same data from `leads.list`, so this is presentation only.
	const { data: session } = authClient.useSession();
	// The community manager gets this same route, but strictly to browse the
	// orders they themselves registered — no bulk actions, status changes,
	// templates or export. The server already refuses to hand them anything
	// else (`leadsReadProcedure`); this just keeps the buttons that would
	// 403 off the screen.
	const readOnly = isCommunityManager(session?.user?.role);
	// Deleting an order is admin-only and irreversible — the server refuses it
	// for anyone else (`leads.remove`), this keeps the button off their screen.
	const canDelete = canDeleteLeads(session?.user?.role);
	const queryClient = useQueryClient();
	const navigate = useNavigate({ from: Route.fullPath });
	const search = Route.useSearch();

	const sortBy: LeadSortField = search.sortBy ?? "createdAt";
	const sortDir = search.sortDir ?? "desc";
	const page = search.page ?? 1;
	const pageSize = search.pageSize ?? 25;

	// The filter half of the search params, without the paging/sorting keys.
	const filters = useMemo<LeadFilters>(() => {
		const { sortBy: _a, sortDir: _b, page: _c, pageSize: _d, ...rest } = search;
		return rest;
	}, [search]);

	// The text box is uncontrolled by the URL while typing, then debounced into
	// it — otherwise every keystroke would push a history entry and refetch.
	const [searchDraft, setSearchDraft] = useState(search.search ?? "");
	useEffect(() => {
		setSearchDraft(search.search ?? "");
	}, [search.search]);
	useEffect(() => {
		const trimmed = searchDraft.trim();
		if (trimmed === (search.search ?? "")) return;
		const timer = setTimeout(() => {
			void navigate({
				search: (prev) => ({
					...prev,
					search: trimmed || undefined,
					page: undefined,
				}),
				replace: true,
			});
		}, 300);
		return () => clearTimeout(timer);
	}, [searchDraft, search.search, navigate]);

	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [activeLead, setActiveLead] = useState<LeadRow | null>(null);
	const [templateLead, setTemplateLead] = useState<LeadRow | null>(null);
	const [showTemplates, setShowTemplates] = useState(false);
	const [showExport, setShowExport] = useState(false);

	const trainingsQuery = useQuery(trpc.trainings.list.queryOptions());
	const trainings = trainingsQuery.data ?? [];
	const defaultTraining = trainings.find((t) => t.isDefault) ?? trainings[0];
	const activeTraining = trainings.find((t) => t.id === filters.trainingId);

	// First load with no ?trainingId in the URL: land on the default training
	// instead of showing every training's leads mixed together.
	useEffect(() => {
		if (filters.trainingId || !defaultTraining) return;
		void navigate({
			search: (prev) => ({ ...prev, trainingId: defaultTraining.id }),
			replace: true,
		});
	}, [filters.trainingId, defaultTraining, navigate]);

	const listQuery = useQuery({
		...trpc.leads.list.queryOptions({
			...filters,
			sortBy,
			sortDir,
			page,
			pageSize,
		}),
		// Keeps the previous page on screen while the next one loads instead of
		// collapsing the table to a spinner on every filter tweak.
		placeholderData: keepPreviousData,
		enabled: Boolean(filters.trainingId),
	});
	const countsQuery = useQuery({
		...trpc.leads.counts.queryOptions(filters),
		enabled: Boolean(filters.trainingId),
	});
	const facetsQuery = useQuery({
		...trpc.leads.facets.queryOptions(filters),
		enabled: Boolean(filters.trainingId),
	});
	// Untreated-lead badges on the training switcher — unfiltered on purpose, so
	// it reports the other trainings too (see `newByTraining` on the server).
	const newByTrainingQuery = useQuery(trpc.leads.newByTraining.queryOptions());
	// Off-limits to the community manager on the server too (`pipelineProcedure`)
	// — skip the request rather than let it 403 in the console.
	const usersQuery = useQuery({
		...trpc.users.list.queryOptions(),
		enabled: !readOnly,
	});
	// Runs of the training on screen, newest first — the edition filter's
	// options and the runs a bulk confirmation can name.
	const editionsQuery = useQuery(trpc.editions.list.queryOptions());
	const trainingEditions = (editionsQuery.data ?? [])
		.filter((edition) => edition.trainingId === filters.trainingId)
		.sort((a, b) => b.editionNumber - a.editionNumber);
	const editionOptions: Option[] = trainingEditions.length
		? [
				...trainingEditions.map((edition) => ({
					value: String(edition.courseNumber),
					label: `${editionLabel(edition.editionNumber)} · ${isoDay(edition.startDate)}`,
					count: edition._count.leads,
				})),
				{ value: NO_EDITION, label: NO_EDITION_LABEL },
			]
		: [];

	const removeMutation = useMutation({
		...trpc.leads.remove.mutationOptions(),
		onSuccess: () => {
			// Same three lists the bulk bar refreshes: the page itself, the status
			// chip counts, and the untreated badges on the training switcher.
			void queryClient.invalidateQueries({
				queryKey: trpc.leads.list.queryKey(),
			});
			void queryClient.invalidateQueries({
				queryKey: trpc.leads.counts.queryKey(),
			});
			void queryClient.invalidateQueries({
				queryKey: trpc.leads.newByTraining.queryKey(),
			});
		},
	});

	const rows = (listQuery.data?.rows ?? []) as LeadRow[];
	const total = listQuery.data?.total ?? 0;
	const pageCount = listQuery.data?.pageCount ?? 1;

	const users = usersQuery.data?.users ?? [];
	const currentUser = users.find(
		(u) => u.id === usersQuery.data?.currentUserId,
	);
	const userOptions: Option[] = users.map((u) => ({
		value: u.id,
		label: u.name,
	}));

	const facets = {
		wilayas: (facetsQuery.data?.wilayas ?? []).map((f) => ({
			value: f.value,
			label: f.value,
			count: f.count,
		})),
		jobStatuses: (facetsQuery.data?.jobStatuses ?? []).map((f) => ({
			value: f.value,
			label: f.value,
			count: f.count,
		})),
		sources: (facetsQuery.data?.sources ?? []).map((f) => ({
			value: f.value,
			label: f.value,
			count: f.count,
		})),
	};

	function patchSearch(patch: Partial<LeadsSearch>) {
		void navigate({
			search: (prev) => {
				const next = { ...prev, ...patch };
				// Any filter change resets to page 1 — page 7 of a narrower result
				// set is almost always empty.
				if (!("page" in patch)) next.page = undefined;
				for (const [key, value] of Object.entries(next)) {
					if (
						value === undefined ||
						value === "" ||
						(Array.isArray(value) && value.length === 0)
					) {
						delete next[key as keyof LeadsSearch];
					}
				}
				return next;
			},
		});
		setSelected(new Set());
	}

	function toggleSort(field: LeadSortField) {
		if (sortBy === field) {
			patchSearch({ sortDir: sortDir === "asc" ? "desc" : "asc" });
		} else {
			// Names read best A→Z; dates newest-first.
			patchSearch({
				sortBy: field,
				sortDir: field === "fullName" || field === "wilaya" ? "asc" : "desc",
			});
		}
	}

	function toggleRow(id: string) {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}

	function handleDelete(lead: LeadRow) {
		if (
			!window.confirm(
				`حذف طلب "${lead.fullName}" نهائياً؟ سيُحذف معه سجل المتابعة بالكامل.`,
			)
		) {
			return;
		}
		// Drop it from the selection and close its modal first — leaving a
		// deleted id selected would arm the bulk bar on a row that no longer
		// exists.
		setSelected((prev) => {
			if (!prev.has(lead.id)) return prev;
			const next = new Set(prev);
			next.delete(lead.id);
			return next;
		});
		setActiveLead((prev) => (prev?.id === lead.id ? null : prev));
		setTemplateLead((prev) => (prev?.id === lead.id ? null : prev));
		removeMutation.mutate({ id: lead.id });
	}

	const allOnPageSelected =
		rows.length > 0 && rows.every((row) => selected.has(row.id));

	const activeFilterCount = countActiveFilters(filters);
	const today = algiersToday();
	// Still resolving which training to default to — treat like "loading" so
	// the empty-state message doesn't flash before the redirect effect fires.
	const isLoading = listQuery.isLoading || !filters.trainingId;
	// Read-only drops the checkbox and actions columns — 7 instead of 9.
	const columnCount = readOnly ? 7 : 9;

	return (
		<div className="flex flex-col gap-5">
			<header className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<h1 className="text-2xl font-bold text-navy">
						{readOnly ? "طلباتي" : "طلبات التسجيل"}
					</h1>
					<p className="text-sm text-muted">
						{isLoading ? (
							"جارٍ التحميل…"
						) : (
							<>
								{total.toLocaleString("ar-DZ")} طلب
								{activeFilterCount ? " مطابق للفلاتر" : " مسجَّل"}
								{countsQuery.data && activeFilterCount
									? ` من ${countsQuery.data.total.toLocaleString("ar-DZ")}`
									: ""}
								{readOnly ? " سجّلتَها بنفسك" : ""}
							</>
						)}
					</p>
				</div>

				<div className="flex flex-wrap items-center gap-2">
					{!readOnly ? (
						<>
							<button
								type="button"
								onClick={() => setShowTemplates(true)}
								className="inline-flex items-center gap-2 rounded-lg border border-line bg-white px-4 py-2 text-sm font-bold text-navy transition-colors hover:bg-canvas"
							>
								<MessageSquareText className="h-4 w-4" />
								قوالب الرسائل
							</button>
							<button
								type="button"
								onClick={() => setShowExport(true)}
								className="inline-flex items-center gap-2 rounded-lg border border-teal bg-teal-50 px-4 py-2 text-sm font-bold text-teal-600 transition-colors hover:bg-teal hover:text-white"
							>
								<FileSpreadsheet className="h-4 w-4" />
								تصدير Excel
							</button>
						</>
					) : null}
					{activeTraining ? (
						<a
							href={`/brochure/${activeTraining.slug}`}
							target="_blank"
							rel="noreferrer"
							className="inline-flex items-center gap-2 rounded-lg border border-line bg-white px-4 py-2 text-sm font-bold text-navy transition-colors hover:bg-canvas"
						>
							<FileText className="h-4 w-4" />
							البطاقة التقنية
						</a>
					) : null}
				</div>
			</header>

			{trainings.length > 1 ? (
				<TrainingSwitcher
					trainings={trainings}
					newCounts={newByTrainingQuery.data ?? {}}
					selected={filters.trainingId}
					// Editions belong to one training, so that filter can't carry over.
					onSelect={(trainingId) =>
						patchSearch({ trainingId, editions: undefined })
					}
				/>
			) : null}

			<StatusChips
				counts={countsQuery.data?.byStatus}
				total={countsQuery.data?.total ?? 0}
				selected={filters.statuses}
				onSelect={(statuses) => patchSearch({ statuses })}
			/>

			{/* toolbar */}
			<div className="flex flex-col gap-3 rounded-2xl border border-line bg-white p-4 shadow-card">
				<div className="flex flex-wrap items-center gap-2">
					<SearchInput value={searchDraft} onChange={setSearchDraft} />
					{currentUser ? (
						<button
							type="button"
							onClick={() =>
								patchSearch({
									assignedTo: filters.assignedTo?.includes(currentUser.id)
										? undefined
										: [currentUser.id],
								})
							}
							className={`whitespace-nowrap rounded-xl border px-3 py-2 text-sm font-bold transition-colors ${
								filters.assignedTo?.includes(currentUser.id)
									? "border-teal bg-teal-50 text-teal-600"
									: "border-line bg-white text-navy hover:bg-canvas"
							}`}
						>
							طلباتي
						</button>
					) : null}
				</div>

				<FilterFields
					filters={filters}
					onPatch={(patch) => patchSearch(patch)}
					facets={facets}
					users={userOptions}
					editions={editionOptions}
					statusCounts={countsQuery.data?.byStatus}
				/>

				<div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-3">
					<div className="flex flex-wrap items-center gap-2 text-xs text-muted">
						<span>ترتيب:</span>
						<select
							value={sortBy}
							onChange={(e) =>
								patchSearch({ sortBy: e.target.value as LeadSortField })
							}
							className="rounded-lg border border-line bg-white px-2 py-1.5 text-xs font-semibold text-navy outline-none focus:border-teal"
						>
							{LEAD_SORT_FIELDS.map((field) => (
								<option key={field} value={field}>
									{SORT_LABELS[field]}
								</option>
							))}
						</select>
						<button
							type="button"
							onClick={() =>
								patchSearch({ sortDir: sortDir === "asc" ? "desc" : "asc" })
							}
							className="rounded-lg border border-line bg-white p-1.5 text-navy transition-colors hover:bg-canvas"
							aria-label="عكس الترتيب"
						>
							{sortDir === "asc" ? (
								<ArrowUp className="h-3.5 w-3.5" />
							) : (
								<ArrowDown className="h-3.5 w-3.5" />
							)}
						</button>

						<span className="mx-1 h-4 w-px bg-line" />

						<span>لكل صفحة:</span>
						<select
							value={pageSize}
							onChange={(e) =>
								patchSearch({ pageSize: Number(e.target.value) })
							}
							className="rounded-lg border border-line bg-white px-2 py-1.5 text-xs font-semibold text-navy outline-none focus:border-teal"
						>
							{PAGE_SIZES.map((size) => (
								<option key={size} value={size}>
									{size}
								</option>
							))}
						</select>
					</div>

					{activeFilterCount ? (
						<button
							type="button"
							onClick={() => void navigate({ search: {}, replace: true })}
							className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-bold text-muted transition-colors hover:bg-canvas hover:text-navy"
						>
							<X className="h-3.5 w-3.5" />
							مسح كل الفلاتر ({activeFilterCount})
						</button>
					) : null}
				</div>
			</div>

			{listQuery.isError ? (
				<p className="rounded-xl bg-red-50 p-4 text-sm font-semibold text-red-700">
					تعذر تحميل الطلبات. حاول تحديث الصفحة.
				</p>
			) : null}

			{/* table */}
			<div className="relative overflow-x-auto rounded-2xl border border-line bg-white shadow-card">
				{listQuery.isFetching && !listQuery.isLoading ? (
					<div className="absolute inset-x-0 top-0 h-0.5 overflow-hidden bg-teal-50">
						<div className="h-full w-1/3 animate-[loading_1s_ease-in-out_infinite] bg-teal" />
					</div>
				) : null}

				<table className="w-full min-w-[1000px] text-start text-sm">
					<thead>
						<tr className="border-b border-line bg-canvas text-muted">
							{!readOnly ? (
								<th className="w-10 px-3 py-3">
									<input
										type="checkbox"
										checked={allOnPageSelected}
										onChange={() =>
											setSelected((prev) => {
												const next = new Set(prev);
												if (allOnPageSelected) {
													for (const row of rows) next.delete(row.id);
												} else {
													for (const row of rows) next.add(row.id);
												}
												return next;
											})
										}
										aria-label="تحديد كل الصفحة"
										className="h-4 w-4 accent-teal"
									/>
								</th>
							) : null}
							<SortableHeader
								label="الاسم"
								field="fullName"
								sortBy={sortBy}
								sortDir={sortDir}
								onSort={toggleSort}
							/>
							<th className="px-4 py-3 text-start font-bold">الهاتف</th>
							<SortableHeader
								label="الولاية"
								field="wilaya"
								sortBy={sortBy}
								sortDir={sortDir}
								onSort={toggleSort}
							/>
							<th className="px-4 py-3 text-start font-bold">المسؤولون</th>
							<SortableHeader
								label="تاريخ الطلب"
								field="createdAt"
								sortBy={sortBy}
								sortDir={sortDir}
								onSort={toggleSort}
							/>
							<SortableHeader
								label="المتابعة"
								field="nextFollowUpAt"
								sortBy={sortBy}
								sortDir={sortDir}
								onSort={toggleSort}
							/>
							<SortableHeader
								label="الحالة"
								field="status"
								sortBy={sortBy}
								sortDir={sortDir}
								onSort={toggleSort}
							/>
							{!readOnly ? (
								<th className={`${STICKY_ACTIONS} bg-canvas font-bold`}>
									إجراءات
								</th>
							) : null}
						</tr>
					</thead>
					<tbody>
						{rows.map((lead) => {
							const isSelected = selected.has(lead.id);
							const overdue =
								lead.nextFollowUpAt &&
								new Intl.DateTimeFormat("en-CA", {
									timeZone: "Africa/Algiers",
									year: "numeric",
									month: "2-digit",
									day: "2-digit",
								}).format(new Date(lead.nextFollowUpAt)) < today;

							return (
								<tr
									key={lead.id}
									className={`group border-b border-line transition-colors last:border-0 ${
										isSelected ? "bg-teal-50/50" : "hover:bg-canvas/60"
									}`}
								>
									{!readOnly ? (
										<td className="px-3 py-3">
											<input
												type="checkbox"
												checked={isSelected}
												onChange={() => toggleRow(lead.id)}
												aria-label={`تحديد ${lead.fullName}`}
												className="h-4 w-4 accent-teal"
											/>
										</td>
									) : null}
									<td className="px-4 py-3">
										<span className="block font-semibold text-ink">
											{lead.fullName}
										</span>
										{lead.origin === "INTERNAL" ? (
											<span
												className={`mt-0.5 inline-block rounded border px-1.5 py-0.5 text-[0.65rem] font-semibold ${ORIGIN_STYLES.INTERNAL}`}
												title={
													lead.createdBy
														? `سجّله ${lead.createdBy.name}`
														: ORIGIN_LABELS.INTERNAL
												}
											>
												{ORIGIN_LABELS.INTERNAL}
												{lead.createdBy ? ` · ${lead.createdBy.name}` : ""}
											</span>
										) : null}
										{lead.email ? (
											<span
												className="block truncate text-xs text-muted"
												dir="ltr"
											>
												{lead.email}
											</span>
										) : null}
									</td>
									<td className="px-4 py-3 text-ink" dir="ltr">
										{lead.phone}
									</td>
									<td className="px-4 py-3 text-ink">
										{lead.wilaya ? (
											<span className="whitespace-nowrap">{lead.wilaya}</span>
										) : (
											<span className="text-muted">—</span>
										)}
										{lead.jobStatus ? (
											<span className="block text-xs text-muted">
												{lead.jobStatus}
											</span>
										) : null}
									</td>
									<td className="px-4 py-3">
										{lead.assignees.length ? (
											<span className="flex max-w-48 flex-wrap gap-1">
												{lead.assignees.map((assignee) => (
													<span
														key={assignee.user.id}
														title={ASSIGNEE_SOURCE_HINTS[assignee.source]}
														className={`whitespace-nowrap rounded-md px-2 py-1 text-xs font-semibold ${
															assignee.source === "ACTIVITY"
																? "bg-canvas text-navy"
																: "bg-teal-50 text-teal-600"
														}`}
													>
														{assignee.user.name}
													</span>
												))}
											</span>
										) : (
											<span className="text-xs text-muted">غير مُسند</span>
										)}
									</td>
									<td className="whitespace-nowrap px-4 py-3 text-muted">
										<span className="block text-xs">
											{formatDate(lead.createdAt)}
										</span>
										<span className="block text-[0.7rem] opacity-70">
											{relativeDays(lead.createdAt)}
										</span>
									</td>
									<td className="whitespace-nowrap px-4 py-3">
										{lead.nextFollowUpAt ? (
											<span
												className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold ${
													overdue
														? "bg-red-100 text-red-700"
														: "bg-amber-100 text-amber-800"
												}`}
											>
												<CalendarClock className="h-3 w-3" />
												{formatDay(lead.nextFollowUpAt)}
											</span>
										) : (
											<span className="text-xs text-muted">—</span>
										)}
										{lead.lastContactedAt ? (
											<span className="mt-0.5 block text-[0.7rem] text-muted">
												آخر تواصل {relativeDays(lead.lastContactedAt)}
											</span>
										) : null}
									</td>
									<td className="px-4 py-3">
										{readOnly ? (
											<span
												className={`inline-flex items-center whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs font-bold ${STATUS_STYLES[lead.status] ?? ""}`}
											>
												{STATUS_LABELS[lead.status] ?? lead.status}
											</span>
										) : (
											<button
												type="button"
												onClick={() => setActiveLead(lead)}
												className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs font-bold transition-opacity hover:opacity-80 ${STATUS_STYLES[lead.status] ?? ""}`}
												title="تغيير الحالة وعرض السجل"
											>
												{STATUS_LABELS[lead.status] ?? lead.status}
												<ChevronDown className="h-3.5 w-3.5" />
											</button>
										)}
										{lead.deliveryMode || lead.groupType || lead.edition ? (
											<span className="mt-1 flex flex-wrap gap-1">
												{lead.edition ? (
													<span className="rounded border border-teal/30 bg-teal-50 px-1.5 py-0.5 text-[0.65rem] font-semibold text-teal-700">
														{editionLabel(lead.edition.editionNumber)}
													</span>
												) : null}
												{lead.deliveryMode ? (
													<span
														className={`rounded border px-1.5 py-0.5 text-[0.65rem] font-semibold ${DELIVERY_MODE_STYLES[lead.deliveryMode]}`}
													>
														{DELIVERY_MODE_LABELS[lead.deliveryMode]}
													</span>
												) : null}
												{lead.groupType ? (
													<span
														className={`rounded border px-1.5 py-0.5 text-[0.65rem] font-semibold ${GROUP_TYPE_STYLES[lead.groupType]}`}
													>
														{GROUP_TYPE_LABELS[lead.groupType]}
													</span>
												) : null}
											</span>
										) : null}
									</td>
									{!readOnly ? (
										<td
											className={`${STICKY_ACTIONS} ${
												isSelected
													? "bg-teal-50"
													: "bg-white group-hover:bg-canvas"
											}`}
										>
											<div className="flex items-center gap-1.5">
												<a
													href={`tel:${lead.phone}`}
													className="rounded-lg border border-line p-2 text-navy transition-colors hover:bg-canvas"
													title="اتصال"
												>
													<Phone className="h-4 w-4" />
												</a>
												<a
													href={whatsAppLink(lead.phone)}
													target="_blank"
													rel="noreferrer"
													className="rounded-lg border border-[#25D366] p-2 text-[#25D366] transition-colors hover:bg-[#25D366]/10"
													title="واتساب — محادثة فارغة"
												>
													<WhatsAppIcon />
												</a>
												<button
													type="button"
													onClick={() => setTemplateLead(lead)}
													className="rounded-lg border border-[#25D366] bg-[#25D366] p-2 text-white transition-colors hover:bg-[#1eb455]"
													title="إرسال رسالة من قالب"
												>
													<Send className="h-4 w-4" />
												</button>
												{canDelete ? (
													<button
														type="button"
														onClick={() => handleDelete(lead)}
														disabled={
															removeMutation.isPending &&
															removeMutation.variables?.id === lead.id
														}
														className="rounded-lg border border-line p-2 text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
														title="حذف الطلب نهائياً"
													>
														{removeMutation.isPending &&
														removeMutation.variables?.id === lead.id ? (
															<Loader2 className="h-4 w-4 animate-spin" />
														) : (
															<Trash2 className="h-4 w-4" />
														)}
													</button>
												) : null}
											</div>
										</td>
									) : null}
								</tr>
							);
						})}

						{isLoading ? (
							<tr>
								<td colSpan={columnCount} className="px-4 py-16 text-center">
									<Loader2 className="mx-auto h-5 w-5 animate-spin text-muted" />
								</td>
							</tr>
						) : null}

						{!isLoading && rows.length === 0 ? (
							<tr>
								<td
									colSpan={columnCount}
									className="px-4 py-16 text-center text-muted"
								>
									{activeFilterCount ? (
										<>
											<p className="mb-2">لا توجد طلبات مطابقة للفلاتر.</p>
											<button
												type="button"
												onClick={() => void navigate({ search: {} })}
												className="text-sm font-bold text-teal hover:underline"
											>
												مسح الفلاتر
											</button>
										</>
									) : (
										"لا توجد طلبات تسجيل بعد."
									)}
								</td>
							</tr>
						) : null}
					</tbody>
				</table>
			</div>

			{/* pagination */}
			{pageCount > 1 ? (
				<div className="flex flex-wrap items-center justify-between gap-3">
					<p className="text-xs text-muted">
						الصفحة {page} من {pageCount} · {total.toLocaleString("ar-DZ")} طلب
					</p>
					<div className="flex items-center gap-1">
						<PageButton
							disabled={page <= 1}
							onClick={() => patchSearch({ page: page - 1 })}
							label="السابق"
							icon={<ChevronRight className="h-4 w-4" />}
						/>
						<PageButton
							disabled={page >= pageCount}
							onClick={() => patchSearch({ page: page + 1 })}
							label="التالي"
							icon={<ChevronLeft className="h-4 w-4" />}
							iconFirst={false}
						/>
					</div>
				</div>
			) : null}

			{!readOnly && selected.size > 0 ? (
				<BulkActionsBar
					selectedIds={[...selected]}
					selectedWithoutEdition={
						rows.filter((row) => selected.has(row.id) && row.editionId == null)
							.length
					}
					users={users}
					editions={trainingEditions}
					onClear={() => setSelected(new Set())}
				/>
			) : null}

			{activeLead ? (
				<LeadStatusModal
					// The live row, so responsibles added or removed in the dialog show
					// up there as soon as the list refetches.
					lead={rows.find((row) => row.id === activeLead.id) ?? activeLead}
					onClose={() => setActiveLead(null)}
				/>
			) : null}

			{templateLead ? (
				<SendTemplateDialog
					lead={templateLead}
					senderName={currentUser?.name}
					onClose={() => setTemplateLead(null)}
					onManageTemplates={() => {
						setTemplateLead(null);
						setShowTemplates(true);
					}}
				/>
			) : null}

			{showTemplates ? (
				<TemplateManagerDialog
					senderName={currentUser?.name}
					onClose={() => setShowTemplates(false)}
				/>
			) : null}

			{showExport ? (
				<ExportDialog
					initialFilters={filters}
					initialSort={{ sortBy, sortDir }}
					facets={facets}
					users={userOptions}
					editions={editionOptions}
					availableColumns={exportColumnsFor(session?.user?.role)}
					onClose={() => setShowExport(false)}
				/>
			) : null}
		</div>
	);
}

/** The "N جديد" pill — how many leads of a training nobody has treated yet. */
function NewLeadsBadge({ count }: { count: number }) {
	return (
		<span className="shrink-0 rounded-full bg-teal-700 px-2 py-0.5 text-xs font-bold text-white tabular-nums">
			{count.toLocaleString("ar-DZ")} {STATUS_LABELS.NEW}
		</span>
	);
}

/**
 * One training's leads at a time — switching resets every other filter's scope.
 *
 * A native <select> can't carry the per-training badge, so this is the same
 * popover pattern the filter toolbar uses. Each row shows how many NEW leads
 * are waiting there and the list leads with the biggest pile, so a rep opening
 * the page can go straight to whatever needs treating first.
 */
function TrainingSwitcher({
	trainings,
	newCounts,
	selected,
	onSelect,
}: {
	trainings: Array<{ id: string; name: string; isDefault: boolean }>;
	newCounts: Record<string, number>;
	selected: string | undefined;
	onSelect: (trainingId: string) => void;
}) {
	const [open, setOpen] = useState(false);
	const ref = useDismiss(() => setOpen(false));

	// Most untreated leads first. Ties fall back to the default training and
	// then the name, so the order stays stable while the counts are loading.
	const ordered = [...trainings].sort(
		(a, b) =>
			(newCounts[b.id] ?? 0) - (newCounts[a.id] ?? 0) ||
			Number(b.isDefault) - Number(a.isDefault) ||
			a.name.localeCompare(b.name, "ar"),
	);
	const active = trainings.find((t) => t.id === selected);
	const activeNew = active ? (newCounts[active.id] ?? 0) : 0;

	return (
		// The wrapper may shrink, so the 20rem button's max-w-full has something
		// to cap it against on a phone.
		<div className="flex min-w-0 max-w-full items-center gap-2 text-sm font-bold text-navy">
			التدريب:
			<div className="relative min-w-0" ref={ref}>
				<button
					type="button"
					onClick={() => setOpen((v) => !v)}
					aria-expanded={open}
					className="inline-flex w-80 max-w-full items-center gap-2 rounded-xl border border-line bg-white px-3 py-2 text-sm font-bold text-navy transition-colors hover:bg-canvas"
				>
					<span className="flex-1 truncate text-start">
						{active ? active.name : "اختر تدريباً"}
						{active?.isDefault ? " (افتراضي)" : ""}
					</span>
					{activeNew ? <NewLeadsBadge count={activeNew} /> : null}
					<ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
				</button>

				{open ? (
					<div className="absolute z-30 mt-1 max-h-72 w-full overflow-y-auto rounded-xl border border-line bg-white p-1 shadow-lift">
						{ordered.map((training) => {
							const isActive = training.id === selected;
							const count = newCounts[training.id] ?? 0;
							return (
								<button
									key={training.id}
									type="button"
									aria-current={isActive}
									onClick={() => {
										setOpen(false);
										if (!isActive) onSelect(training.id);
									}}
									className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-start text-sm transition-colors ${
										isActive
											? "bg-teal-50 font-bold text-teal-600"
											: "font-semibold text-ink hover:bg-canvas"
									}`}
								>
									<span className="flex-1 truncate">
										{training.name}
										{training.isDefault ? " (افتراضي)" : ""}
									</span>
									{count ? <NewLeadsBadge count={count} /> : null}
								</button>
							);
						})}
					</div>
				) : null}
			</div>
		</div>
	);
}

/**
 * The count dashboard. Each chip is also a one-click status filter, and the
 * counts stay computed against the *other* active filters so they always
 * predict what a click will show.
 */
function StatusChips({
	counts,
	total,
	selected,
	onSelect,
}: {
	counts?: Record<string, number>;
	total: number;
	selected: LeadStatus[] | undefined;
	onSelect: (next: LeadStatus[] | undefined) => void;
}) {
	if (!counts) {
		return <div className="h-9 animate-pulse rounded-xl bg-line/40" />;
	}

	const isAll = !selected?.length;

	return (
		// One swipeable row on phones — wrapped, the chips alone filled a whole
		// screen before the filters and the table. Wraps as before from sm up.
		<div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible sm:pb-0">
			<button
				type="button"
				onClick={() => onSelect(undefined)}
				className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${
					isAll
						? "bg-navy text-white"
						: "bg-white text-navy ring-1 ring-line hover:bg-canvas"
				}`}
			>
				الكل {total.toLocaleString("ar-DZ")}
			</button>

			{STATUS_CATEGORIES.flatMap((cat) => cat.statuses)
				// A status nobody has ever used is noise in a chip row — the full
				// list stays available in the status dropdown.
				.filter((status) => (counts[status] ?? 0) > 0)
				.map((status) => {
					const active = selected?.includes(status) ?? false;
					return (
						<button
							key={status}
							type="button"
							onClick={() => {
								// Clicking a chip focuses that status; ctrl-free toggling keeps
								// it to one intent per click.
								onSelect(active ? undefined : [status]);
							}}
							title={STATUS_LABELS[status]}
							className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${
								active
									? `${STATUS_STYLES[status]} ring-2 ring-teal ring-offset-1`
									: `${STATUS_STYLES[status]} opacity-80 hover:opacity-100`
							}`}
						>
							<span className="opacity-70">{STATUS_CODES[status]}</span>{" "}
							{STATUS_LABELS[status]} {counts[status]}
						</button>
					);
				})}
		</div>
	);
}

function SortableHeader({
	label,
	field,
	sortBy,
	sortDir,
	onSort,
}: {
	label: string;
	field: LeadSortField;
	sortBy: LeadSortField;
	sortDir: "asc" | "desc";
	onSort: (field: LeadSortField) => void;
}) {
	const active = sortBy === field;
	return (
		<th className="px-4 py-3 text-start font-bold">
			<button
				type="button"
				onClick={() => onSort(field)}
				className={`inline-flex items-center gap-1 transition-colors hover:text-navy ${
					active ? "text-navy" : ""
				}`}
			>
				{label}
				{active ? (
					sortDir === "asc" ? (
						<ArrowUp className="h-3 w-3" />
					) : (
						<ArrowDown className="h-3 w-3" />
					)
				) : (
					<ArrowDown className="h-3 w-3 opacity-25" />
				)}
			</button>
		</th>
	);
}

function PageButton({
	disabled,
	onClick,
	label,
	icon,
	iconFirst = true,
}: {
	disabled: boolean;
	onClick: () => void;
	label: string;
	icon: React.ReactNode;
	iconFirst?: boolean;
}) {
	return (
		<button
			type="button"
			disabled={disabled}
			onClick={onClick}
			className="inline-flex items-center gap-1 rounded-lg border border-line bg-white px-3 py-2 text-xs font-bold text-navy transition-colors hover:bg-canvas disabled:pointer-events-none disabled:opacity-40"
		>
			{iconFirst ? icon : null}
			{label}
			{iconFirst ? null : icon}
		</button>
	);
}

function WhatsAppIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="currentColor"
			className="h-4 w-4"
			role="img"
			aria-labelledby="whatsapp-icon-title"
		>
			<title id="whatsapp-icon-title">واتساب</title>
			<path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.46 1.32 4.96L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2Zm5.8 14.02c-.24.68-1.4 1.32-1.93 1.4-.5.08-1.11.11-1.79-.11-.41-.13-.94-.31-1.62-.6-2.85-1.23-4.71-4.08-4.85-4.27-.14-.19-1.16-1.54-1.16-2.94 0-1.4.73-2.08 1-2.37.24-.28.53-.34.7-.34h.51c.16 0 .38-.03.59.45.24.55.8 1.9.87 2.04.07.14.11.3.02.49-.09.19-.14.3-.28.46-.14.16-.29.36-.42.48-.14.14-.28.28-.12.55.16.28.72 1.19 1.55 1.93 1.06.95 1.96 1.24 2.24 1.38.28.14.44.12.6-.07.16-.19.7-.81.88-1.09.18-.28.36-.23.6-.14.24.09 1.55.73 1.81.86.27.14.45.2.51.32.07.12.07.68-.17 1.35Z" />
		</svg>
	);
}
